import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import { createStyles, IconButton, Theme, useTheme } from '@material-ui/core';
import LastPageIcon from '@material-ui/icons/LastPage';
import TableFooter from '@material-ui/core/TableFooter';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { IData, IColumn, TablePaginationActionsProps } from '../type';

const useStyles1 = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexShrink: 0,
      marginLeft: theme.spacing(2.5),
    },
  })
);

const tableContainerStyle = makeStyles(() =>
  createStyles({
    root: {
      width: '200em',
      height: '20em',
      overflowX: 'hidden',
      overflowY: 'scroll',
    },
  })
);

const tableCellStyle = makeStyles(() =>
  createStyles({
    root: {
      fontSize: '1.1em',
      fontWeight: 'bold',
      backgroundColor: '#e0e0e0',
    },
  })
);

const TablePaginationActions = (props: TablePaginationActionsProps) => {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
};

const MyTable = (props: { data: IData[] }): JSX.Element => {
  const { data = [] } = props;
  const tableContainerClasses = tableContainerStyle();
  const tableCellClasses = tableCellStyle();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    data.length > 100 ? 100 : data.length
  );
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rows: IData[] = data.map((v) => {
    return { ...v };
  });

  const columns: IColumn[] = [
    { label: '項次', id: 'count' },
    { label: '到櫃日期', id: 'etaDate' },
    { label: '船隻掛號', id: 'vesselRegNo' },
    { label: '主號', id: 'mawbNo' },
    { label: '拆櫃日期', id: 'declDate' },
    { label: '記帳代號', id: 'expressId' },
    { label: '業者', id: 'boxName' },
    { label: '路徑', id: 'path' },
    { label: '報單筆數', id: 'hwbNum' },
    { label: '件數', id: 'pieceNum' },
    { label: '重量', id: 'weight' },
    {
      label: 'C1',
      id: 'c1',
      style: { borderLeft: '1px solid' },
    },
    { label: 'C2', id: 'c2' },
    { label: 'C3', id: 'c3', style: { borderRight: '1px solid' } },
    {
      label: 'AKE',
      id: 'ake',
    },
    { label: 'Size20', id: 'size20' },
    { label: 'Size40', id: 'size40', style: { borderRight: '1px solid' } },
    { label: '貨櫃號碼', id: 'containerNo' },
    { label: '封條號碼', id: 'sealNo' },
    { label: '入倉', id: 'impPiece' },
    { label: '扣倉', id: 'detainPiece' },
    { label: '出倉', id: 'outPiece' },
    { label: '破損件數', id: 'breakageNumber' },
  ];

  return (
    <>
      <Paper>
        <div style={{ overflowX: 'scroll' }}>
          <TableContainer className={tableContainerClasses.root}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    className={tableCellClasses.root}
                    colSpan={11}
                    align="center"
                  />
                  <TableCell
                    className={tableCellClasses.root}
                    colSpan={3}
                    align="center"
                    style={{
                      borderLeft: '1px solid',
                      borderRight: '1px solid',
                    }}
                  >
                    通關方式(含一般及簡易)
                  </TableCell>
                  <TableCell
                    className={tableCellClasses.root}
                    colSpan={3}
                    align="center"
                    style={{
                      borderRight: '1px solid',
                    }}
                  >
                    櫃型
                  </TableCell>
                  <TableCell
                    className={tableCellClasses.root}
                    colSpan={6}
                    align="center"
                  />
                </TableRow>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      className={tableCellClasses.root}
                      key={column.id}
                      align="center"
                      style={{ minWidth: column.minWidth, ...column.style }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {(rowsPerPage > 0
                  ? rows.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : rows
                ).map((row, i) => (
                  <TableRow key={String(i + 1)}>
                    <TableCell align="center">{i + 1}</TableCell>
                    <TableCell align="center">{row.etaDate}</TableCell>
                    <TableCell align="center">{row.vesselRegNo}</TableCell>
                    <TableCell align="center">{row.mawbNo}</TableCell>
                    <TableCell align="center">{row.declDate}</TableCell>
                    <TableCell align="center">{row.expressId}</TableCell>
                    <TableCell align="center">{row.boxName}</TableCell>
                    <TableCell align="center">{row.path}</TableCell>
                    <TableCell align="center">{row.hwbNum}</TableCell>
                    <TableCell align="center">{row.pieceNum}</TableCell>
                    <TableCell align="center">{row.weight}</TableCell>
                    <TableCell align="center">{row.c1}</TableCell>
                    <TableCell align="center">{row.c2}</TableCell>
                    <TableCell align="center">{row.c3}</TableCell>
                    <TableCell align="center">{row.ake}</TableCell>
                    <TableCell align="center">{row.size20}</TableCell>
                    <TableCell align="center">{row.size40}</TableCell>
                    <TableCell align="center">{row.containerNo}</TableCell>
                    <TableCell align="center">{row.sealNo}</TableCell>
                    <TableCell align="center">{row.impPiece}</TableCell>
                    <TableCell align="center">{row.detainPiece}</TableCell>
                    <TableCell align="center">{row.outPiece}</TableCell>
                    <TableCell align="center">{row.breakageNumber}</TableCell>
                  </TableRow>
                ))}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow />
              </TableFooter>
            </Table>
          </TableContainer>
        </div>
        <TablePagination
          labelRowsPerPage="筆數"
          component="div"
          rowsPerPageOptions={[
            { label: '全部', value: data.length },
            10,
            50,
            100,
          ]}
          colSpan={3}
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
            native: true,
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
    </>
  );
};
export default MyTable;
